import React from 'react';
import { Placement } from '@noths/polaris-client-ribbons-design-system';

import { ImagePreloadLink } from 'src/components/atoms/ImagePreloadLink';
import { RichTextEditor } from 'src/components/atoms/RichTextEditor';
import { Seo } from 'src/components/atoms/Seo';
import { Carousel } from 'src/components/molecules/Carousel';
import { ImageCard } from 'src/components/molecules/ImageCard';
import { HomepageBanner } from 'src/components/organisms/HomepageBanner';
import { HomepageHeroBanner } from 'src/components/organisms/HomepageHeroBanner';
import { HomepagePartnerBlock } from 'src/components/organisms/HomepagePartnerBlock';
import { RecentlyViewed } from 'src/components/organisms/RecentlyViewed';
import { UspBanner } from 'src/components/organisms/UspBanner';
import { VideoBlock } from 'src/components/organisms/VideoBlock';
import { homepageLinkedData } from 'src/constants/jsonLinkedData';
import { useHomepageRichRelevanceData } from 'src/hooks/useHomepageRichRelevanceData';
import * as styles from 'src/styles/homepage.styles';
import { Row } from 'src/styles/layout';
import * as stylesLayout from 'src/styles/layout.styles';
import { color } from 'src/styles/noths.theme';
import {
  trackCarouselProductClick,
  trackHeroBlockClick,
  trackVideoClick,
} from 'src/tracking/homepageTracking';
import { PageProps } from 'src/types/PageProps';
import { LegacyHomepageData } from './types';

const testName = 'Homepage';

export type LegacyHomepageProps = Omit<PageProps, 'page'> & {
  page: LegacyHomepageData;
};

export const LegacyHomepage = ({
  page: {
    pageTitle,
    seo,
    uspBanner,
    heroBanner,
    heroBlocks,
    slider,
    videoBlock,
    partners,
    banner,
    infoSection,
  },
  richRelevanceSessionId,
  functionalCookiesConsented,
  currency,
}: LegacyHomepageProps) => {
  const handleVideoClick = () => {
    const {
      heading,
      video: { youtubeID },
    } = videoBlock;

    trackVideoClick({
      pageTitle,
      sectionNumber: 6,
      lastSectionTitle: heading.toUpperCase() || undefined,
      blockType: 'video block',
      youtubeID,
    });
  };

  const richRelevanceData = useHomepageRichRelevanceData({
    placementType: Placement.HomepageRecentlyViewed,
    sessionId: richRelevanceSessionId,
    hasDisabledCookies: !functionalCookiesConsented,
  });

  return (
    <div css={stylesLayout.wrapper}>
      {heroBanner.bannerImage && (
        <>
          <ImagePreloadLink href={heroBanner.bannerImage.mobile} />
          <ImagePreloadLink href={heroBanner.bannerImage.desktop} desktop />
        </>
      )}
      <Seo {...seo} linkedData={homepageLinkedData} />
      <div css={stylesLayout.Container} data-wide={true}>
        <h1 css={styles.pageTitle}>{pageTitle}</h1>

        <section css={stylesLayout.Section}>
          <UspBanner slides={uspBanner} />
        </section>

        <section id="section-1" css={stylesLayout.Section}>
          <HomepageHeroBanner {...heroBanner} testName={testName} />
        </section>

        <section id="section-2" css={stylesLayout.Section}>
          <div css={styles.HeroCtaWrapper}>
            <Row>
              {heroBlocks.map(({ title, image, link }, blockIndex) => (
                // @ts-ignore
                <Row.Column size={{ phone: 6, tablet: 3 }} key={title}>
                  <ImageCard
                    key={title}
                    title={title}
                    image={image}
                    link={link}
                    trackGtmEvent={() =>
                      trackHeroBlockClick({
                        sectionId: 2,
                        blockIndex,
                        ctaText: title,
                        link: link,
                      })
                    }
                  />
                </Row.Column>
              ))}
            </Row>
          </div>
        </section>

        <span
          css={stylesLayout.BorderLine}
          style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
        />

        <section id="section-3" css={stylesLayout.Section} data-testid={`${testName}-SectionThree`}>
          <h2 css={stylesLayout.Heading}>{slider.heading}</h2>
          <div css={styles.CarouselWrapper}>
            <Carousel
              renderSlideComponents={() =>
                slider.slides.map(({ title, image, link }, slideIndex) => (
                  <ImageCard
                    key={title}
                    title={title}
                    image={image}
                    link={link}
                    trackGtmEvent={() => {
                      trackCarouselProductClick({
                        sectionId: 3,
                        slideIndex,
                        ctaText: title,
                        link: link,
                        sectionTitle: slider.heading,
                      });
                    }}
                  />
                ))
              }
            />
          </div>
        </section>

        <span
          css={stylesLayout.BorderLine}
          style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
        />
        <section css={stylesLayout.Section} id="section-5">
          <RecentlyViewed
            currency={currency}
            items={richRelevanceData?.items}
            placementTitle={richRelevanceData?.message}
            placementType={Placement.HomepageRecentlyViewed}
            strategy={richRelevanceData?.strategy}
          />
        </section>

        <span
          css={stylesLayout.BorderLine}
          style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
        />

        {videoBlock.shouldDisplay && (
          <>
            <section id="section-6" css={stylesLayout.Section}>
              <VideoBlock
                {...videoBlock}
                headingStyles={stylesLayout.Heading}
                noPadding
                customTrackClick={handleVideoClick}
              />
            </section>
            <span
              css={stylesLayout.BorderLine}
              style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
            />
          </>
        )}

        <section id="section-7" css={stylesLayout.Section} data-double-padding={true}>
          <HomepagePartnerBlock {...partners} sectionId="7" />
        </section>

        <span
          css={stylesLayout.BorderLine}
          style={{ '--gutter': '-15px' } as stylesLayout.CSSVarBorderLine}
        />

        <section id="section-8" css={stylesLayout.Section}>
          <HomepageBanner {...banner} sectionId="8" />
        </section>

        {infoSection.copy && (
          <section css={stylesLayout.Section}>
            <div css={styles.InfoSection}>
              <h2 css={stylesLayout.Heading} data-custom-heading="true">
                {infoSection.heading}
              </h2>
              <RichTextEditor isSmallText copyColor={color.black} html={infoSection.copy} />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
